/**
 * DailyBriefArchivePage — Feb 2026
 *
 * Pinned at `/dispatch/admin/daily-brief-archive`. Lists every persisted
 * morning brief (newest first) so dispatch can scroll back through prior
 * days without digging through email. Selecting a day shows the same
 * tile + drill-down structure used by the email template.
 *
 * Backend endpoints:
 *   GET /api/admin/daily-brief-archive              — index (thin)
 *   GET /api/admin/daily-brief-archive/{et_date}    — full snapshot
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, ChevronRight, Mail, Download } from 'lucide-react';
import { CustomerTierDistribution } from '../../components/CustomerTierDistribution';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const TILE_COLORS = {
  delivered: '#39FF14',
  placed:    '#00F0FF',
  revenue:   '#FF9F0A',
  new:       '#BF5AF2',
  points:    '#FFD60A',
  cancelled: '#FF3B30',
};


function StatTile({ label, value, color, hint }) {
  return (
    <div
      className="border bg-[#0C0612] px-3 py-3"
      style={{ borderColor: color }}
      data-testid={`brief-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.14em]" style={{ color }}>
        {label}
      </div>
      <div className="font-mono text-2xl text-white mt-1">{value}</div>
      {hint && <div className="text-[#6B8E73] text-xs mt-1">{hint}</div>}
    </div>
  );
}


function DetailSection({ title, count, color, children }) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div className="border border-[#2A0F3D] bg-[#0A0008] mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#11091A]"
        data-testid={`brief-detail-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="font-mono text-xs uppercase tracking-[0.12em]" style={{ color }}>
          {open ? '▾' : '▸'} {title}
          <span className="ml-2 text-[#6B8E73] normal-case tracking-normal">({count})</span>
        </span>
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}


function RegionChip({ region }) {
  if (!region) return null;
  return (
    <span className="ml-2 inline-block px-1.5 py-px bg-[#1A1129] border border-[#2A0F3D] font-mono text-[9px] uppercase tracking-wider text-[#A3C7A8]">
      {region}
    </span>
  );
}


function UserLink({ userId, label, fallback }) {
  const text = label || fallback || '(unknown)';
  if (!userId) return <span>{text}</span>;
  return (
    <Link
      to={`/dispatch/admin/users?openUser=${userId}&from=daily-brief`}
      className="text-white hover:text-[#39FF14] border-b border-dotted border-[#39FF14]/30"
    >
      {text}
    </Link>
  );
}


function OrderLink({ orderId }) {
  if (!orderId) return null;
  return (
    <Link
      to={`/dispatch/orders?focus=${orderId}`}
      className="ml-2 font-mono text-[10px] text-[#A3C7A8] border-b border-dotted border-[#A3C7A8]/30 hover:text-[#39FF14]"
    >
      #{orderId.slice(0, 8).toUpperCase()}
    </Link>
  );
}


export default function DailyBriefArchivePage() {
  const [briefs, setBriefs] = useState([]);
  const [selected, setSelected] = useState(null);   // { ...snapshot }
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchIndex = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/daily-brief-archive?limit=60`, { withCredentials: true });
      setBriefs(data?.briefs || []);
      // Auto-select most recent
      if (!selectedDate && data?.briefs?.length > 0) {
        setSelectedDate(data.briefs[0].et_date);
      }
    } catch (e) {
      toast.error(`Failed to load archive: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchDetail = useCallback(async (et_date) => {
    if (!et_date) return;
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/daily-brief-archive/${et_date}`, { withCredentials: true });
      setSelected(data);
    } catch (e) {
      toast.error(`Failed to load brief detail: ${e?.response?.data?.detail || e.message}`);
      setSelected(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => { fetchIndex(); }, [fetchIndex]);
  useEffect(() => { fetchDetail(selectedDate); }, [selectedDate, fetchDetail]);

  const yest = selected?.data_snapshot?.yesterday || {};
  const detail = yest.detail || {};

  const prettyDate = useMemo(() => {
    if (!selected?.et_date) return '';
    try {
      const d = new Date(selected.et_date + 'T12:00:00Z');
      return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return selected.et_date;
    }
  }, [selected]);

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="daily-brief-archive">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/dispatch" className="font-mono text-[10px] uppercase tracking-wider text-[#A3C7A8] hover:text-[#39FF14]">
              ← BACK TO DISPATCH
            </Link>
            <h1 className="font-mono text-2xl text-[#39FF14] mt-2">DAILY BRIEF ARCHIVE</h1>
            <p className="text-[#A3C7A8] text-sm mt-1">
              Every morning brief, persisted. Tap any date to expand its drill-downs.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Index list */}
          <div className="space-y-4">
            <CustomerTierDistribution compact />
            <div className="border border-[#2A0F3D] bg-[#0A0008]">
            <div className="px-3 py-2 border-b border-[#2A0F3D] font-mono text-[10px] uppercase tracking-wider text-[#39FF14] flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {briefs.length} brief{briefs.length === 1 ? '' : 's'}
              {loading && <span className="ml-auto text-[#A3C7A8]">loading...</span>}
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {briefs.length === 0 && !loading && (
                <div className="px-3 py-4 text-[#6B8E73] font-mono text-xs">No briefs yet.</div>
              )}
              {briefs.map((b) => (
                <button
                  type="button"
                  key={b.et_date}
                  onClick={() => setSelectedDate(b.et_date)}
                  className={`w-full text-left px-3 py-2.5 border-b border-[#2A0F3D] hover:bg-[#11091A] transition-colors ${b.et_date === selectedDate ? 'bg-[#1A1129]' : ''}`}
                  data-testid={`brief-list-row-${b.et_date}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-white">{b.et_date}</span>
                    <ChevronRight className="w-3 h-3 text-[#39FF14]" />
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] font-mono">
                    <span className="text-[#39FF14]">{b.delivered}d</span>
                    <span className="text-[#00F0FF]">{b.placed}p</span>
                    <span className="text-[#FF9F0A]">${b.revenue?.toFixed(0)}</span>
                    {b.cancelled > 0 && <span className="text-[#FF3B30]">{b.cancelled}c</span>}
                    {b.forced && <span className="text-[#BF5AF2] uppercase">forced</span>}
                  </div>
                </button>
              ))}
            </div>
            </div>
          </div>

          {/* Detail panel */}
          <div>
            {!selected && !loadingDetail && (
              <div className="border border-[#2A0F3D] bg-[#0A0008] p-8 text-center text-[#6B8E73] font-mono text-sm">
                Select a date to view the brief.
              </div>
            )}
            {loadingDetail && (
              <div className="border border-[#2A0F3D] bg-[#0A0008] p-8 text-center text-[#39FF14] font-mono text-sm">
                <span className="cursor-blink">█</span> Loading brief...
              </div>
            )}
            {selected && (
              <div className="border border-[#2A0F3D] bg-[#0A0008]">
                {/* Header */}
                <div className="px-4 py-3 border-b border-[#2A0F3D] flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#39FF14]">YESTERDAY AT A GLANCE</div>
                    <div className="font-mono text-sm text-white mt-0.5" data-testid="brief-archive-date">{prettyDate}</div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#A3C7A8]">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {selected.recipients?.length || 0} sent</span>
                    {selected.forced && <span className="text-[#BF5AF2] font-mono text-[10px] uppercase">FORCED</span>}
                  </div>
                </div>

                {/* Tile grid */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                  <StatTile label="DELIVERED" value={yest.delivered ?? 0} color={TILE_COLORS.delivered} />
                  <StatTile label="PLACED" value={yest.placed ?? 0} color={TILE_COLORS.placed} />
                  <StatTile label="REVENUE" value={`$${(yest.revenue ?? 0).toFixed(2)}`} color={TILE_COLORS.revenue} />
                  <StatTile label="NEW CUSTOMERS" value={yest.new_signups ?? 0} color={TILE_COLORS.new} />
                  <StatTile label="POINTS GRANTED" value={(yest.points_granted ?? 0).toLocaleString()} color={TILE_COLORS.points} />
                  <StatTile label="CANCELLED" value={yest.cancelled ?? 0} color={TILE_COLORS.cancelled} />
                </div>

                <div className="px-4 pb-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#6B8E73] mb-2">
                    Tap a section to expand ↓
                  </div>

                  <DetailSection title="Delivered" count={detail?.delivered?.length || 0} color={TILE_COLORS.delivered}>
                    {(detail?.delivered || []).map((o) => (
                      <div key={o.order_id} className="py-1.5 text-sm border-b border-[#1A1129] last:border-b-0">
                        <UserLink userId={o.user_id} label={o.customer_name} fallback={o.customer_email} />
                        <RegionChip region={o.region} />
                        <span className="ml-2 font-mono text-xs text-[#39FF14]">${o.total?.toFixed(2)}</span>
                        <OrderLink orderId={o.order_id} />
                        {o.driver_name && <span className="ml-2 text-[#6B8E73] text-xs">· {o.driver_name}</span>}
                      </div>
                    ))}
                  </DetailSection>

                  <DetailSection title="Placed" count={detail?.placed?.length || 0} color={TILE_COLORS.placed}>
                    {(detail?.placed || []).map((o) => (
                      <div key={o.order_id} className="py-1.5 text-sm border-b border-[#1A1129] last:border-b-0">
                        <UserLink userId={o.user_id} label={o.customer_name} fallback={o.customer_email} />
                        <RegionChip region={o.region} />
                        <span className="ml-2 font-mono text-xs text-white">${o.total?.toFixed(2)}</span>
                        <OrderLink orderId={o.order_id} />
                        {o.status && <span className="ml-2 text-[#6B8E73] text-xs">· {o.status}</span>}
                      </div>
                    ))}
                  </DetailSection>

                  <DetailSection title="New customers" count={detail?.new_customers?.length || 0} color={TILE_COLORS.new}>
                    {(detail?.new_customers || []).map((u) => (
                      <div key={u.user_id} className="py-1.5 text-sm border-b border-[#1A1129] last:border-b-0">
                        <UserLink userId={u.user_id} label={u.name} fallback={u.email} />
                        <RegionChip region={u.region} />
                        {u.referred && <span className="ml-2 font-mono text-[10px] uppercase text-[#FFD60A]">REFERRED</span>}
                        {u.email && u.name && <span className="ml-2 text-[#6B8E73] text-xs">{u.email}</span>}
                      </div>
                    ))}
                  </DetailSection>

                  <DetailSection title="Points granted" count={detail?.points_grants?.length || 0} color={TILE_COLORS.points}>
                    {(detail?.points_grants || []).map((g, i) => (
                      <div key={i} className="py-1.5 text-sm border-b border-[#1A1129] last:border-b-0">
                        <span className="font-mono text-sm text-[#39FF14] font-bold">+{g.delta}</span>
                        <span className="mx-2">·</span>
                        <UserLink userId={g.user_id} label={g.customer_name} fallback={g.customer_email} />
                        <RegionChip region={g.region} />
                        <span className="ml-2 font-mono text-[10px] uppercase text-[#A3C7A8]">{g.source}</span>
                        {g.reason && <span className="ml-2 text-[#6B8E73] text-xs">— {g.reason}</span>}
                        {g.order_id && <OrderLink orderId={g.order_id} />}
                      </div>
                    ))}
                  </DetailSection>

                  <DetailSection title="Cancelled" count={detail?.cancelled?.length || 0} color={TILE_COLORS.cancelled}>
                    {(detail?.cancelled || []).map((o) => (
                      <div key={o.order_id} className="py-1.5 text-sm border-b border-[#1A1129] last:border-b-0">
                        <UserLink userId={o.user_id} label={o.customer_name} fallback={o.customer_email} />
                        <RegionChip region={o.region} />
                        <span className="ml-2 font-mono text-xs text-[#FF3B30]">${o.total?.toFixed(2)}</span>
                        <OrderLink orderId={o.order_id} />
                        {o.reason && <span className="ml-2 text-[#6B8E73] text-xs">· {o.reason}</span>}
                      </div>
                    ))}
                  </DetailSection>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
